import {
  ChatFeedbackProvider,
  type SubmittedRating,
  type InlineFeedbackState,
  type InlineFeedbackHandlers,
  type FeedbackActionType,
} from '@terra/ui-design-system';
import { useCallback, useMemo, useState } from 'react';
import { useCoPilotFeedback } from './useCoPilotFeedback';
import { useOptionalTargetId } from '../../../store/projectStore';
import { useFeedbackDraftsStore } from '../../../store/feedbackDraftsStore';

type FeedbackProviderProps = {
  isEnabled: boolean;
  children: React.ReactNode;
  chatId: string;
};

type PendingRating = 'positive' | 'negative';

const FEEDBACK_ERROR_MESSAGE = 'Failed to submit feedback. Please try again.';
const FEEDBACK_EMPTY_MESSAGE = 'Please enter some feedback';

export function FeedbackProvider({
  isEnabled,
  children,
  chatId,
}: FeedbackProviderProps) {
  const targetId = useOptionalTargetId();

  const [submittingTraces, setSubmittingTraces] = useState<
    Record<string, boolean>
  >({});
  const [submittedRatings, setSubmittedRatings] = useState<
    Record<string, SubmittedRating>
  >({});
  const [activeTraceId, setActiveTraceId] = useState<string | null>(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [pendingRating, setPendingRating] = useState<PendingRating | null>(
    null,
  );

  const { getDraft, updateFeedbackText, clearDraft } = useFeedbackDraftsStore();

  const { handleQuickRating, handleFeedbackSubmit } = useCoPilotFeedback({
    targetId: targetId ?? null,
    threadId: chatId,
  });

  const markTraceSubmitting = useCallback(
    (traceId: string, value: boolean) =>
      setSubmittingTraces((prev) => ({ ...prev, [traceId]: value })),
    [],
  );

  const markTraceRating = useCallback(
    (traceId: string, rating: SubmittedRating) =>
      setSubmittedRatings((prev) => ({ ...prev, [traceId]: rating })),
    [],
  );

  const resetInlineState = useCallback(() => {
    setActiveTraceId(null);
    setFeedbackText('');
    setSubmitError(null);
    setPendingRating(null);
  }, []);

  const openInlineFeedback = useCallback(
    (traceId: string, rating: PendingRating | null) => {
      setPendingRating(rating);
      setActiveTraceId(traceId);
      setSubmitError(null);

      const draft = getDraft(traceId);
      setFeedbackText(draft?.feedback ?? '');
    },
    [getDraft],
  );

  const submitQuickRatingForTrace = useCallback(
    async (traceId: string, rating: PendingRating) => {
      markTraceSubmitting(traceId, true);
      try {
        const result = await handleQuickRating({ traceId, rating });
        if (result?.success) {
          markTraceRating(traceId, rating);
        }
      } finally {
        markTraceSubmitting(traceId, false);
      }
    },
    [handleQuickRating, markTraceSubmitting, markTraceRating],
  );

  const onFeedbackClick = useCallback(
    async ({
      traceId,
      actionType,
    }: {
      traceId: string;
      actionType: FeedbackActionType;
    }) => {
      if (actionType === 'positive') {
        await submitQuickRatingForTrace(traceId, 'positive');
        return;
      }

      openInlineFeedback(
        traceId,
        actionType === 'negative' ? 'negative' : null,
      );
    },
    [submitQuickRatingForTrace, openInlineFeedback],
  );

  const onTextChange = useCallback(
    (text: string) => {
      setFeedbackText(text);
      if (activeTraceId) {
        updateFeedbackText(activeTraceId, text);
      }
    },
    [activeTraceId, updateFeedbackText],
  );

  const onSubmit = useCallback(async () => {
    const trimmedFeedback = feedbackText.trim();
    if (!trimmedFeedback) {
      setSubmitError(FEEDBACK_EMPTY_MESSAGE);
      return;
    }
    if (!activeTraceId) return;

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const result = await handleFeedbackSubmit({
        traceId: activeTraceId,
        feedback: trimmedFeedback,
        rating: pendingRating,
      });

      if (result?.success) {
        if (pendingRating) {
          markTraceRating(activeTraceId, pendingRating);
        }
        clearDraft();
        resetInlineState();
      } else {
        setSubmitError(FEEDBACK_ERROR_MESSAGE);
      }
    } catch {
      setSubmitError(FEEDBACK_ERROR_MESSAGE);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    feedbackText,
    activeTraceId,
    handleFeedbackSubmit,
    pendingRating,
    markTraceRating,
    clearDraft,
    resetInlineState,
  ]);

  const onSkip = useCallback(async () => {
    if (!activeTraceId) return;

    if (pendingRating) {
      await submitQuickRatingForTrace(activeTraceId, pendingRating);
    }

    clearDraft();
    resetInlineState();
  }, [
    activeTraceId,
    pendingRating,
    submitQuickRatingForTrace,
    clearDraft,
    resetInlineState,
  ]);

  const onCancel = useCallback(() => {
    resetInlineState();
  }, [resetInlineState]);

  const inlineFeedback = useMemo<InlineFeedbackState>(
    () => ({
      activeTraceId,
      text: feedbackText,
      submitError,
      isSubmitting,
      hasPendingRating: pendingRating !== null,
    }),
    [activeTraceId, feedbackText, submitError, isSubmitting, pendingRating],
  );

  const inlineFeedbackHandlers = useMemo<InlineFeedbackHandlers>(
    () => ({
      onTextChange,
      onSubmit,
      onSkip,
      onCancel,
    }),
    [onTextChange, onSubmit, onSkip, onCancel],
  );

  return (
    <ChatFeedbackProvider
      isEnabled={isEnabled}
      submittingTraces={submittingTraces}
      submittedRatings={submittedRatings}
      onFeedbackClick={onFeedbackClick}
      inlineFeedback={inlineFeedback}
      inlineFeedbackHandlers={inlineFeedbackHandlers}
    >
      {children}
    </ChatFeedbackProvider>
  );
}
